import { useId, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { SearchField } from '@/components/search-field/SearchField'
import { matterPaths } from '@/routing/paths'
import { isExternalStorageLinkedFile } from '@/user_files/files/store/selectors'
import { isExternalStorageSyncedFolder } from '@/user_files/folders/store/selectors'
import { kMaxSearchQueryLength } from '../constants'
import { useActiveOption } from '../hooks/useActiveOption'
import { useRecentSearches } from '../hooks/useRecentSearches'
import { useSearchSuggestions, type SearchSuggestion } from '../hooks/useSearchSuggestions'
import { RecentSearches } from './RecentSearches'
import { SearchSuggestions } from './SearchSuggestions'

type MatterFilesSearchBarProps = {
  /** Current input value, owned by `useMatterFilesSearch`. */
  query: string
  onQueryChange: (value: string) => void
  /** Commits a query, replacing the folder listing with results. Defaults to the input value. */
  onSubmit: (value?: string) => void
  /** Returns to browsing and empties the field. */
  onClearSearch: () => void
  /** Opens a synced folder in the DMS source view, where synced contents are actually listed. */
  onOpenSyncedFolder?: (folderId: string) => void
  /** Selects the Paxton source before navigating to a matter-owned suggestion. */
  onOpenMatterFolder?: () => void
}

/**
 * The search field and the dropdown beneath it.
 *
 * Search runs in stages, and the field shows whichever one applies: recent searches on an empty
 * field, typeahead suggestions once there is a query, and committed results in the table on Enter.
 * This component owns the seams — Enter takes the highlighted option if there is one, and commits
 * the query otherwise.
 */
export function MatterFilesSearchBar({ query, onQueryChange, onSubmit, onClearSearch, onOpenSyncedFolder, onOpenMatterFolder }: MatterFilesSearchBarProps) {
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()
  const [, setSearchParams] = useSearchParams()
  const listboxId = useId()

  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [recentsDismissed, setRecentsDismissed] = useState(false)

  const suggestions = useSearchSuggestions({ query, isFocused })
  const { recents, record, clear } = useRecentSearches(matterId ?? null)

  // Recents answer "what did I search before", so they only make sense on an empty field; the
  // moment there is a query, suggestions for that query are the more useful answer.
  const showRecents = isFocused && !recentsDismissed && !suggestions.isOpen && query.trim() === '' && recents.length > 0
  const recentsCursor = useActiveOption(showRecents ? recents.length : 0, 'recents')

  const isDropdownOpen = suggestions.isOpen || showRecents
  const activeIndex = suggestions.isOpen ? suggestions.activeIndex : recentsCursor.activeIndex
  const hasActiveOption = suggestions.isOpen ? suggestions.activeIndex !== -1 : Boolean(recents[recentsCursor.activeIndex])

  const getOptionId = (index: number) => `${listboxId}-option-${index}`

  const closeDropdown = (committedQuery?: string) => {
    suggestions.dismiss(committedQuery)
    setRecentsDismissed(true)
  }

  const handleClearHistory = () => {
    clear()
    // Clearing empties the list, which unmounts the dropdown and the button with it. Without this
    // a keyboard user is dropped onto `body` and restarts the tab order from the top of the page.
    inputRef.current?.focus()
  }

  const runQuery = (value: string) => {
    // Dismiss for the query being committed, not the one in the field: running a recent search
    // replaces the field's contents, and the dropdown must stay shut for the new value too.
    closeDropdown(value)
    record(value)
    onSubmit(value)
  }

  const handleSelect = (suggestion: SearchSuggestion) => {
    closeDropdown()

    // Landing somewhere means the field no longer describes what is on screen. Clearing explicitly
    // matters because selecting an item in the folder already open changes no route, so the
    // navigation-driven reset in `useMatterFilesSearch` would never fire.
    onClearSearch()

    // A folder is its own destination; a file's is the folder holding it. An empty path means the
    // item sits in the matter root.
    const targetFolderId = suggestion.kind === 'folder' ? suggestion.id : suggestion.data.folder_path.at(-1)?.id

    // Synced items are listed by the DMS source view, not the matter one, so routing them through
    // the matter route would land the user on a listing that filters the very row they picked.
    const isSynced = suggestion.kind === 'folder' ? isExternalStorageSyncedFolder(suggestion.data) : isExternalStorageLinkedFile(suggestion.data)

    // Picking a file means "take me to this file", so the location alone is only half the answer —
    // it opens too (Jennifer, #project-files 2026-08-10). The request travels in the URL rather
    // than opening the viewer here: changing folder unmounts the drive subtree while the route
    // handler resolves the new folder, and that unmount clears any session set beforehand. The
    // drive reopens it from this parameter once it is ready, and drops the parameter afterwards.
    const viewFileId = suggestion.kind === 'file' ? suggestion.id : undefined

    if (isSynced && targetFolderId && onOpenSyncedFolder) {
      onOpenSyncedFolder(targetFolderId)

      // No route change here — the synced view is not routed — so only the parameter is added.
      if (viewFileId) {
        setSearchParams(
          (params) => {
            params.set('viewUserFile', viewFileId)
            return params
          },
          { replace: true }
        )
      }

      return
    }

    if (!matterId) return

    onOpenMatterFolder?.()
    const destination = targetFolderId ? matterPaths.userFiles.folder(matterId, targetFolderId) : matterPaths.userFiles.root(matterId)
    navigate(viewFileId ? `${destination}?viewUserFile=${encodeURIComponent(viewFileId)}` : destination)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // Stop the caret from jumping to the other end of the input while walking the list.
      event.preventDefault()

      // Either arrow brings a dismissed list back, so neither key is swallowed with nothing to
      // show for it. Which list returns depends on whether there is a query to suggest against.
      if (!isDropdownOpen) {
        suggestions.open()
        setRecentsDismissed(false)
        return
      }

      const delta = event.key === 'ArrowDown' ? 1 : -1
      if (suggestions.isOpen) suggestions.moveActiveIndex(delta)
      else recentsCursor.moveActiveIndex(delta)
      return
    }

    if (event.key === 'Enter') {
      // The "All search results" row names no item, so with it highlighted this falls through to
      // committing the query, the same as Enter with nothing highlighted.
      if (suggestions.isOpen) {
        const active = suggestions.items[suggestions.activeIndex]
        if (active) {
          handleSelect(active)
          return
        }
      }

      if (showRecents) {
        const activeRecent = recents[recentsCursor.activeIndex]
        if (activeRecent) {
          runQuery(activeRecent)
          return
        }
      }

      runQuery(query)
      return
    }

    if (event.key === 'Escape' && isDropdownOpen) {
      // Keep the query and the focus; the user is dismissing the list, not the search.
      event.preventDefault()
      closeDropdown()
    }
  }

  return (
    <div
      className="relative"
      // Focus is tracked on the container, not the input: the dropdown's Clear History button is a
      // sibling of the field, so tracking the input alone unmounted the list before Tab could reach
      // it. Focus events bubble here, and `relatedTarget` tells us whether focus is still inside.
      onFocus={() => setIsFocused(true)}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return
        setIsFocused(false)
      }}
    >
      <SearchField
        ref={inputRef}
        value={query}
        onChange={(value) => {
          setRecentsDismissed(false)
          onQueryChange(value)
        }}
        onKeyDown={handleKeyDown}
        placeholder="Search matter files"
        aria-label="Search matter files"
        maxLength={kMaxSearchQueryLength}
        role="combobox"
        aria-expanded={isDropdownOpen}
        // Only while the listbox exists — a closed dropdown would leave the IDREF dangling.
        aria-controls={isDropdownOpen ? listboxId : undefined}
        aria-autocomplete="list"
        // Named only while that row is actually rendered — a list holds its highlight through a
        // dismissal, so the open check matters as much as the range one.
        aria-activedescendant={isDropdownOpen && hasActiveOption ? getOptionId(activeIndex) : undefined}
      />

      {suggestions.isOpen && (
        <SearchSuggestions
          files={suggestions.files}
          folders={suggestions.folders}
          query={query}
          activeIndex={suggestions.activeIndex}
          onActiveIndexChange={suggestions.setActiveIndex}
          onSelect={handleSelect}
          onRunSearch={() => runQuery(query)}
          listboxId={listboxId}
          getOptionId={getOptionId}
        />
      )}

      {showRecents && (
        <RecentSearches
          recents={recents}
          activeIndex={recentsCursor.activeIndex}
          onActiveIndexChange={recentsCursor.setActiveIndex}
          onSelect={runQuery}
          onClearHistory={handleClearHistory}
          listboxId={listboxId}
          getOptionId={getOptionId}
        />
      )}
    </div>
  )
}
